import React, { useState, useEffect } from 'react';
import { StandardModal, StandardButton, MODAL_SECTIONS } from './Modal';

const PostLoginPermissions = ({ user, onPermissionsComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [choices, setChoices] = useState({ 
    camera: true, 
    storage: true, 
    notifications: true, 
    location: false, 
    bluetooth: false, 
    biometric: false 
  });
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasShownBefore, setHasShownBefore] = useState(false);

  // Check if we've already shown permissions for this user
  useEffect(() => {
    const checkPermissionHistory = async () => {
      if (!user) return;

      try {
        const { getOrCreateDeviceId } = await import('../services/DeviceService');
        const deviceId = await getOrCreateDeviceId();
        const { Preferences } = await import('@capacitor/preferences');
        const { value } = await Preferences.get({ 
          key: `preferences:permissions/${deviceId}` 
        });

        if (value) {
          const permissionData = JSON.parse(value);
          if (permissionData.skipped || permissionData.granted) {
            setHasShownBefore(true);
            return;
          }
        }

        // Show permission dialog after a short delay
        setTimeout(() => {
          setIsOpen(true);
        }, 2000);
      } catch (error) {
        console.error('Error checking permission history:', error);
        // Show dialog anyway if there's an error
        setTimeout(() => {
          setIsOpen(true);
        }, 2000);
      }
    };

    checkPermissionHistory();
  }, [user]);

  const toggle = (key) => setChoices(prev => ({ ...prev, [key]: !prev[key] }));

  const handleAccept = async () => {
    setIsRequesting(true);
    try {
      // Request the selected permissions
      const { requestSelectedPermissions } = await import('../services/PermissionsService');
      const perms = await requestSelectedPermissions(choices);
      console.log('✅ Permission results:', perms);
      
      // Save permission data
      const { getOrCreateDeviceId } = await import('../services/DeviceService');
      const deviceId = await getOrCreateDeviceId();
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ 
        key: `preferences:permissions/${deviceId}`, 
        value: JSON.stringify({ 
          granted: true, 
          permissions: perms, 
          timestamp: Date.now() 
        }) 
      });

      // Handle biometric if selected
      if (choices.biometric) {
        try { 
          const { default: BiometricAuth } = await import('../services/BiometricAuth');
          await BiometricAuth.enableBiometric(); 
          console.log('🔐 Biometric auth enabled');
        } catch (error) {
          console.error('❌ Biometric auth failed:', error);
        }
      }
      
      console.log('🎉 All permissions processed successfully');
      onPermissionsComplete && onPermissionsComplete();
    } catch (error) {
      console.error('❌ Permission request failed:', error);
    }
    setIsRequesting(false);
    setIsOpen(false);
  };

  const handleSkip = async () => {
    try {
      // Save skip preference
      const { getOrCreateDeviceId } = await import('../services/DeviceService');
      const deviceId = await getOrCreateDeviceId();
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ 
        key: `preferences:permissions/${deviceId}`, 
        value: JSON.stringify({ 
          skipped: true, 
          timestamp: Date.now() 
        }) 
      });
      console.log('💾 Permission skip preference saved');
    } catch (error) {
      console.warn('Failed to save skip preference:', error);
    }
    onPermissionsComplete && onPermissionsComplete();
    setIsOpen(false);
  };

  const handleSkipForever = async () => {
    try {
      // Save skip forever preference
      const { getOrCreateDeviceId } = await import('../services/DeviceService');
      const deviceId = await getOrCreateDeviceId();
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ 
        key: `preferences:permissions/${deviceId}`, 
        value: JSON.stringify({ 
          skippedForever: true, 
          timestamp: Date.now() 
        }) 
      });
      console.log('💾 Permission skip forever preference saved');
    } catch (error) {
      console.warn('Failed to save skip forever preference:', error);
    }
    onPermissionsComplete && onPermissionsComplete();
    setIsOpen(false);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isRequesting) {
        handleSkip();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isRequesting]);

  // Don't show if we've already shown before or no user
  if (!user || hasShownBefore) {
    return null;
  }

  return (
    <StandardModal section={MODAL_SECTIONS.SETTINGS} 
      isOpen={isOpen} 
      onClose={handleSkip} 
      title="🔐 Welcome to ACCTOO - Permissions Setup"
      size="lg"
      showCloseButton={!isRequesting}
    >
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Welcome to ACCTOO!
          </h3>
          <p className="text-gray-600 mb-4">
            To provide you with the best experience, we need a few permissions. 
            You can change these later in settings.
          </p>
        </div>

        {isRequesting && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
              <p className="text-sm text-blue-800">
                Setting up permissions for your device...
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Essential Permissions:</h4>
          
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input 
              type="checkbox" 
              checked={choices.camera} 
              onChange={() => toggle('camera')}
              className="w-4 h-4 text-blue-600"
            />
            <div className="flex items-center">
              <span className="text-2xl mr-3">📷</span>
              <div>
                <span className="font-medium">Camera</span>
                <p className="text-sm text-gray-600">For barcode scanning and photo capture</p>
              </div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input 
              type="checkbox" 
              checked={choices.storage} 
              onChange={() => toggle('storage')}
              className="w-4 h-4 text-blue-600"
            />
            <div className="flex items-center">
              <span className="text-2xl mr-3">💾</span>
              <div>
                <span className="font-medium">Storage & Photos</span>
                <p className="text-sm text-gray-600">For file uploads and exports</p>
              </div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input 
              type="checkbox" 
              checked={choices.notifications} 
              onChange={() => toggle('notifications')}
              className="w-4 h-4 text-blue-600"
            />
            <div className="flex items-center">
              <span className="text-2xl mr-3">🔔</span>
              <div>
                <span className="font-medium">Notifications</span>
                <p className="text-sm text-gray-600">For alerts and reminders</p>
              </div>
            </div>
          </label>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Optional Permissions:</h4>
          
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input 
              type="checkbox" 
              checked={choices.location} 
              onChange={() => toggle('location')}
              className="w-4 h-4 text-blue-600"
            />
            <div className="flex items-center">
              <span className="text-2xl mr-3">📍</span>
              <div>
                <span className="font-medium">Location</span>
                <p className="text-sm text-gray-600">For location-based features (optional)</p>
              </div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input 
              type="checkbox" 
              checked={choices.biometric} 
              onChange={() => toggle('biometric')}
              className="w-4 h-4 text-blue-600"
            />
            <div className="flex items-center">
              <span className="text-2xl mr-3">🔐</span>
              <div>
                <span className="font-medium">Biometric Lock</span>
                <p className="text-sm text-gray-600">For fingerprint/face unlock (optional)</p>
              </div>
            </div>
          </label>
        </div>

        <div className="flex gap-3 pt-4">
          <StandardButton 
            onClick={handleSkip} 
            variant="secondary" 
            className="flex-1"
            disabled={isRequesting}
          >
            Skip for Now
          </StandardButton>
          <StandardButton 
            onClick={handleAccept} 
            className="flex-1"
            disabled={isRequesting}
          >
            {isRequesting ? 'Setting up...' : 'Allow Selected'}
          </StandardButton>
        </div>
        
        <div className="text-center pt-2 space-y-2">
          <button
            onClick={handleSkip}
            disabled={isRequesting}
            className="text-sm text-gray-500 hover:text-gray-700 underline disabled:opacity-50 block"
          >
            Skip for now
          </button>
          <button
            onClick={handleSkipForever}
            disabled={isRequesting}
            className="text-sm text-red-500 hover:text-red-700 underline disabled:opacity-50 block"
          >
            Skip forever
          </button>
        </div>
      </div>
    </StandardModal>
  );
};

export default PostLoginPermissions;
